import { createSelector } from 'reselect';
import get from 'lodash/get';
import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import has from 'lodash/has';
import sortByKey from '../../utils/sorting';
import { LAST_MESSAGE_SORTING } from '../../constants/props';

import { lastUserIdSelector } from '../board/selectors/settings';

import {
  FAMILY_CHANNEL,
  ACTIVITIES_CHANNEL,
  NOTIFICATIONS_CHANNEL,
  PRIVATE_CHANNEL,
  GROUP_CHANNEL,
  ICONS,
} from '../../constants/chat';

import { CHAT } from '../../constants/strings';
import { hasProviderProfileSelector } from '../context/selectors';
import { APPOINTMENT_MENU } from '../../constants';

const ATTR = 'totalUnreadCount';
const CHAT_ATTR = 'unreadCount';

// helpers

const getGroupChannels = channels => {
  const arrayOfChannels = Object.keys(channels).map(key => channels[key]);

  return sortByKey(arrayOfChannels, LAST_MESSAGE_SORTING);
};

// selectors
export const chatConnectionSelector = state => state.chatConnection;
export const connectionOpenedSelector = state => state.chatConnection.opened;
export const connectionReadySelector = state => state.chatConnection.ready;
export const chatModuleInstanceSelector = state =>
  state.chatConnection.chatModuleInstance;
export const availableChannelsSelector = state =>
  state.chatConnection.availableChannels;
export const joinedChannelsSelector = state =>
  state.chatConnection.joinedChannels;
export const uploadsSelector = state => state.chatConnection.uploads;
export const downloadsSelector = state => state.chatConnection.downloads;
export const lastActiveChannelsSelector = state =>
  state.chatConnection.lastActiveChannelIds;
export const allChatSelector = state => state.board.channels;
export const activeUnreadCountSelector = state =>
  state.chatConnection.activeUnreadCount;
// active channel selectors
export const activeChannelIdSelector = createSelector(
  chatConnectionSelector,
  chat => get(chat, 'activeChannelId'),
);

export const activeChannelSelector = createSelector(
  joinedChannelsSelector,
  activeChannelIdSelector,
  (channels, id) => get(channels, id),
);

const CHAT_TAB_CONSTANTS = {
  [FAMILY_CHANNEL]: {
    title: CHAT.titles.familyChatTab,
    icon: ICONS.generalChatTab,
  },
  [ACTIVITIES_CHANNEL]: {
    title: CHAT.titles.activitiesChatTab,
    icon: ICONS.activitiesChatTab,
  },
  [NOTIFICATIONS_CHANNEL]: {
    title: CHAT.titles.notificationsChatTab,
    icon: ICONS.notificationsChatTab,
  },
  [PRIVATE_CHANNEL]: {
    icon: ICONS.generalChatTab,
  },
  [GROUP_CHANNEL]: {
    icon: ICONS.generalChatTab,
  },
};

export const availableTargetUserChannelsSelector = createSelector(
  availableChannelsSelector,
  lastUserIdSelector,
  joinedChannelsSelector,
  (apiChannels, userId, chatChannels) => {
    const channels = get(apiChannels, [userId], {});

    if (!channels || isEmpty(channels)) return null;

    const connectedChannels = Object.keys(channels).reduce((acc, key) => {
      const apiChannel = get(channels, [key]);
      const chatChannel = get(chatChannels, [key]);

      const unreads = chatChannel
        ? get(chatChannel, [CHAT_ATTR])
        : get(channels, [key, ATTR]);

      const lastMessageAt =
        get(chatChannel, 'lastMessageAt') || get(apiChannel, 'lastMessageAt');
      const inboxTaskId =
        get(chatChannel, 'inboxTaskId') || get(apiChannel, 'inboxTaskId');

      acc[key] = {
        ...apiChannel,
        [ATTR]: unreads,
        lastMessageAt,
        inboxTaskId,
      };

      return acc;
    }, {});

    return connectedChannels;
  },
);

export const availableSortedTargetUserChannelsSelector = createSelector(
  availableTargetUserChannelsSelector,
  channels => {
    if (isEmpty(channels)) return [];

    return uniq([...getGroupChannels(channels)].filter(Boolean));
  },
);

export const availableTargetUserChannelsDataSelector = createSelector(
  availableSortedTargetUserChannelsSelector,
  hasProviderProfileSelector,
  (channels, isProvider) => {
    const defaultChannels = channels.map(channel => {
      const channelName = camelCase(channel.channelName);

      const icon =
        channel.type === 'system'
          ? get(CHAT_TAB_CONSTANTS, [channelName, 'icon'], 'community')
          : get(CHAT_TAB_CONSTANTS, [channel.type, 'icon'], 'community');

      const title =
        channel.type === 'system'
          ? get(CHAT_TAB_CONSTANTS, [channelName, 'title'], channelName)
          : channel.channelName;

      return {
        ...channel,
        icon,
        title,
      };
    });

    return isProvider
      ? [APPOINTMENT_MENU, ...defaultChannels]
      : [...defaultChannels];
  },
);

export const nextActiveTargetUserChannelSelector = createSelector(
  availableChannelsSelector,
  lastUserIdSelector,
  activeChannelIdSelector,
  (channels, userId, channelId) => {
    if (isEmpty(channels)) {
      return { channelId };
    }

    if (isEmpty(channels) || !userId || !channelId) return null;

    return get(channels, [userId, channelId], null);
  },
);

export const socialChannelSelector = createSelector(
  allChatSelector,
  channels => {
    return channels.filter(item => has(item, 'contextChannel'));
  },
);

export const socialChatSelector = createSelector(
  socialChannelSelector,
  channels => {
    return channels.map(item => item.contextChannel);
  },
);

export const activeSocialChannelSelector = createSelector(
  socialChatSelector,
  activeChannelIdSelector,
  (channels, channelId) => {
    if (channelId) {
      return channels.find(item => item.channelId === channelId);
    }
    return null;
  },
);

export const activeTargetUserChannelSelector = createSelector(
  joinedChannelsSelector,
  activeChannelIdSelector,
  (channels, channelId) => {
    if (isEmpty(channels) || !channelId) return null;

    return get(channels, channelId, null);
  },
);

export const lastActiveChannelIdSelector = createSelector(
  lastActiveChannelsSelector,
  lastUserIdSelector,
  (channels, userId) => {
    return channels.find(item => {
      return item.targetUserId === userId;
    });
  },
);

// todo: problem is here
export const defaultChannelIdSelector = createSelector(
  availableTargetUserChannelsDataSelector,
  activeChannelIdSelector,
  lastActiveChannelIdSelector,
  (channels, activeChannelId, lastChannel) => {
    if (!channels || isEmpty(channels)) return null;

    const arrayOfChannels = Object.keys(channels).map(key => channels[key]);
    if (arrayOfChannels.find(item => item.channelId === activeChannelId)) {
      return activeChannelId;
    }

    if (lastChannel) {
      return lastChannel.id;
    }

    return get(arrayOfChannels[0], 'channelId');
  },
);

export const activeContextChannelSelector = createSelector(
  availableTargetUserChannelsDataSelector,
  activeChannelIdSelector,
  (channels, channelId) => {
    return channels.find(item => item.channelId === channelId);
  },
);

export const activeSocialChatTitleSelector = createSelector(
  activeSocialChannelSelector,
  channel => {
    return get(channel, 'channelName');
  },
);

export const channelTitleSelector = createSelector(
  activeSocialChatTitleSelector,
  activeContextChannelSelector,
  (social, context) => {
    return social || get(context, 'title');
  },
);

export const isReadOnlyContextSelector = createSelector(
  activeContextChannelSelector,
  channel => {
    const channelName = get(channel, 'channelName');
    return [NOTIFICATIONS_CHANNEL, ACTIVITIES_CHANNEL].includes(channelName);
  },
);

export const isDefaultChannelSelector = createSelector(
  activeContextChannelSelector,
  channel => {
    const channelName = get(channel, 'channelName');
    return [
      NOTIFICATIONS_CHANNEL,
      ACTIVITIES_CHANNEL,
      'family_circle',
    ].includes(channelName);
  },
);

export const isSocialChannelSelector = createSelector(
  activeSocialChannelSelector,
  channel => {
    return !!channel;
  },
);

export const channelBlockedStateSelector = createSelector(
  activeSocialChannelSelector,
  activeContextChannelSelector,
  (social, context) => {
    return get(social || context, 'blocked');
  },
);



// WEBPACK FOOTER //
// ./src/redux/chatConnection/selectors.js